import { useMemo, useState } from 'react';
import type { TableProps } from 'antd';
import { CheckOutlined, CloseOutlined, DeleteOutlined, DownloadOutlined, ExportOutlined } from '@ant-design/icons';
import { Blocks, ClipboardCheck, Code2, Package, Plus, RefreshCw } from 'lucide-react';

import { ForbiddenView } from '../../../components/ForbiddenView';
import { DataTable, MenuThaoTac, PageHeader, TabsTruot } from '../../../components/shared';
import { Badge, Button, Empty } from '../../../components/ui';
import type { BadgeVariant } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useQuanTriToChuc } from '../../ho-so-to-chuc/hooks/useQuanTriToChuc';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { KHONG_CO, dungLuongVN, ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { taiTepTinh } from '../../../lib/tai-tep';
import { useToast } from '../../../lib/toast';
import { useUiStore } from '../../../stores/ui';
import { useModuleDaCai } from '../../module-da-cai/hooks/useModuleDaCai';
import { BadgeTrangThaiPhienBan } from '../../quan-ly-module/components/BadgeTrangThaiPhienBan';
import { HopTuChoiModule } from '../../quan-ly-module/components/HopTuChoiModule';
import { HopUploadModule } from '../../quan-ly-module/components/HopUploadModule';
import type { ModuleVersion } from '../../quan-ly-module/types';
import {
  useBoKhoiTao,
  useCaiModule,
  useChoDuyetToChuc,
  useDuyetModuleToChuc,
  useGoModule,
  useGoiCuaToi,
  useLuotCai,
  useModuleDaDuyet,
  useNopGoiToChuc,
  useQuyenManModule,
  useTuChoiModuleToChuc,
  useXoaModuleCuaToi,
} from '../hooks/useModuleToChuc';
import { duongDanTai } from '../nghiep-vu/bo-khoi-tao';
import { trangThaiCai } from '../nghiep-vu/trang-thai-cai';
import type { TrangThaiCaiHienThi } from '../nghiep-vu/trang-thai-cai';
import type { BoKhoiTao, ModuleDaDuyet } from '../types';

const NHAN_CAI: Record<TrangThaiCaiHienThi, { khoa: KhoaDich; variant: BadgeVariant }> = {
  'da-cai': { khoa: 'moduleToChuc.trangThaiCai.daCai', variant: 'success' },
  'dang-cai': { khoa: 'moduleToChuc.trangThaiCai.dangCai', variant: 'warning' },
  'dang-go': { khoa: 'moduleToChuc.trangThaiCai.dangGo', variant: 'warning' },
  loi: { khoa: 'moduleToChuc.trangThaiCai.loi', variant: 'danger' },
  'chua-cai': { khoa: 'moduleToChuc.trangThaiCai.chuaCai', variant: 'neutral' },
};

/** Trạng thái cài của một dòng; dùng chung cache `useLuotCai` với ô thao tác cùng dòng. */
function useTrangThaiDong(code: string, daCai: boolean, theoDoi: boolean) {
  const luot = useLuotCai(code, theoDoi);
  return { trangThai: trangThaiCai(daCai, luot.data?.status), loi: luot.data?.last_error ?? null };
}

function OTrangThaiCai({ code, daCai, theoDoi }: { code: string; daCai: boolean; theoDoi: boolean }) {
  const { t } = useT();
  const { trangThai, loi } = useTrangThaiDong(code, daCai, theoDoi);
  const nhan = NHAN_CAI[trangThai];
  return (
    <div className="min-w-0">
      <Badge variant={nhan.variant}>{t(nhan.khoa)}</Badge>
      {trangThai === 'loi' && loi && <p className="mt-1 break-words text-2xs text-danger-700">{loi}</p>}
    </div>
  );
}

function OThaoTac({
  module,
  daCai,
  coGiaoDien,
  theoDoi,
  coQuyenCai,
  onCai,
  onGo,
}: {
  module: ModuleDaDuyet;
  daCai: boolean;
  coGiaoDien: boolean;
  theoDoi: boolean;
  coQuyenCai: boolean;
  onCai: () => void;
  onGo: () => void;
}) {
  const { t } = useT();
  const navigate = useDieuHuong();
  const { trangThai } = useTrangThaiDong(module.code, daCai, theoDoi);
  return (
    <MenuThaoTac
      tenBanGhi={module.name}
      muc={[
        {
          khoa: 'mo',
          nhan: t('moduleToChuc.thaoTac.mo'),
          icon: <ExportOutlined />,
          nhom: 'xem',
          hien: trangThai === 'da-cai' && coGiaoDien,
          onChon: () => navigate(`/m/${encodeURIComponent(module.code)}`),
        },
        {
          khoa: 'cai',
          nhan: t(trangThai === 'loi' ? 'moduleToChuc.thaoTac.caiLai' : 'moduleToChuc.thaoTac.cai'),
          icon: <DownloadOutlined />,
          hien: coQuyenCai && (trangThai === 'chua-cai' || trangThai === 'loi'),
          onChon: onCai,
        },
        {
          // Server nhận gỡ lượt ACTIVE hoặc FAILED (ADR-0049 mục 8)
          khoa: 'go',
          nhan: t('moduleToChuc.thaoTac.go'),
          icon: <DeleteOutlined />,
          nhom: 'pha-huy',
          hien: coQuyenCai && (trangThai === 'da-cai' || trangThai === 'loi'),
          onChon: onGo,
        },
      ]}
    />
  );
}

/**
 * Màn Module của cổng tenant (ADR-0096 mục 8): module đã duyệt kèm trạng thái cài và nút Cài, gói chính người dùng đã
 * nộp, hộp nộp gói, và (chỉ quản trị tổ chức, ADR-0115) tab Chờ duyệt: hàng chờ và lịch sử cập nhật phiên bản. Module cài xong thành một nhóm trên thanh điều hướng, ngay dưới nhóm này. Mỗi phần hiện theo
 * quyền `core:module:*` (ADR-0104); không đủ quyền thì ẩn, không hiện mờ (ADR-UI-0005).
 */
export function ModuleToChucView() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const toast = useToast();
  const quyen = useQuyenManModule();
  // Duyệt module do người trong tổ chức nộp là việc của quản trị tổ chức (ADR-0115), không gán qua vai trò
  const quanTri = useQuanTriToChuc();
  const choDuyet = useChoDuyetToChuc(quanTri);
  const duyetModule = useDuyetModuleToChuc();
  const tuChoiModule = useTuChoiModuleToChuc();
  const [tuChoiPhienBan, setTuChoiPhienBan] = useState<ModuleVersion | null>(null);
  const daDuyet = useModuleDaDuyet(quyen.xem);
  const goiCuaToi = useGoiCuaToi(quyen.nop);
  const boKhoiTao = useBoKhoiTao(quyen.nop);
  const daCai = useModuleDaCai();
  const cai = useCaiModule();
  const go = useGoModule();
  const xoaModule = useXoaModuleCuaToi();
  const showConfirm = useUiStore((s) => s.showConfirm);
  const nopGoi = useNopGoiToChuc();
  const [moUpload, setMoUpload] = useState(false);
  const [theoDoi, setTheoDoi] = useState<ReadonlySet<string>>(new Set());

  const maDaCai = useMemo(() => new Map((daCai.data ?? []).map((m) => [m.code, m])), [daCai.data]);

  const batDauCai = (m: ModuleDaDuyet) =>
    cai.mutate(m.code, {
      onSuccess: () => {
        setTheoDoi((cu) => new Set(cu).add(m.code));
        toast.success(t('moduleToChuc.daGuiCai', { ten: m.name }));
      },
      onError: (e) => toast.error(t('moduleToChuc.loiCai', { ten: m.name, loi: dichLoi(e) })),
    });

  const xacNhanGo = (m: ModuleDaDuyet) =>
    showConfirm({
      title: t('moduleToChuc.xnGoTieuDe', { ten: m.name }),
      body: t('moduleToChuc.xnGoThan'),
      nhanXacNhan: t('moduleToChuc.thaoTac.go'),
      onOk: () =>
        go.mutate(m.code, {
          onSuccess: () => {
            setTheoDoi((cu) => new Set(cu).add(m.code));
            toast.success(t('moduleToChuc.daGuiGo', { ten: m.name }));
          },
          onError: (e) => toast.error(t('moduleToChuc.loiGo', { ten: m.name, loi: dichLoi(e) })),
        }),
    });

  const xacNhanXoa = (v: ModuleVersion) =>
    showConfirm({
      title: t('moduleToChuc.xnXoaTieuDe', { ten: v.name }),
      body: t('moduleToChuc.xnXoaThan'),
      nhanXacNhan: t('moduleToChuc.thaoTac.xoa'),
      onOk: () =>
        xoaModule.mutate(v.code, {
          onSuccess: () => toast.success(t('moduleToChuc.daXoa', { ten: v.name })),
          onError: (e) => toast.error(t('moduleToChuc.loiXoa', { ten: v.name, loi: dichLoi(e) })),
        }),
    });

  const xacNhanDuyet = (v: ModuleVersion) =>
    showConfirm({
      title: t('moduleToChuc.choDuyet.xnDuyetTieuDe', { ten: v.name, phienBan: v.version }),
      body: t('moduleToChuc.choDuyet.xnDuyetThan'),
      nhanXacNhan: t('moduleToChuc.choDuyet.nutDuyet', { phienBan: v.version }),
      onOk: () => duyetModule.mutate({ code: v.code, version: v.version }),
    });

  const taiBo = (b: BoKhoiTao) => {
    taiTepTinh(duongDanTai(b), b.file);
    toast.success(t('moduleToChuc.boKhoiTao.daTai', { ten: b.name }));
  };

  const nutNop = quyen.nop ? (
    <Button variant="primary" size="md" icon={<Plus size={15} />} onClick={() => setMoUpload(true)}>
      {t('moduleToChuc.nopGoi')}
    </Button>
  ) : undefined;

  const cotDaDuyet: TableProps<ModuleDaDuyet>['columns'] = [
    {
      title: t('moduleToChuc.cotModule'),
      key: 'module',
      render: (_, m) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-content-primary">{m.name}</p>
          <p className="font-mono text-2xs text-content-muted">{m.code}</p>
          {m.description && <p className="mt-0.5 line-clamp-2 text-xs text-content-secondary">{m.description}</p>}
        </div>
      ),
    },
    { title: t('moduleToChuc.cotNhaPhatTrien'), dataIndex: 'vendor', width: 180, responsive: ['md'] },
    {
      title: t('moduleToChuc.cotPhienBan'),
      dataIndex: 'version',
      width: 110,
      render: (v: string) => <span className="font-mono text-xs text-content-primary">{v}</span>,
    },
    {
      title: t('moduleToChuc.cotTrangThai'),
      key: 'trang-thai',
      width: 180,
      render: (_, m) => <OTrangThaiCai code={m.code} daCai={maDaCai.has(m.code)} theoDoi={theoDoi.has(m.code)} />,
    },
    {
      key: 'thao-tac',
      width: 56,
      render: (_, m) => (
        <OThaoTac
          module={m}
          daCai={maDaCai.has(m.code)}
          coGiaoDien={Boolean(maDaCai.get(m.code)?.frontend_remote)}
          theoDoi={theoDoi.has(m.code)}
          coQuyenCai={quyen.cai}
          onCai={() => batDauCai(m)}
          onGo={() => xacNhanGo(m)}
        />
      ),
    },
  ];

  const cotGoiCuaToi: TableProps<ModuleVersion>['columns'] = [
    {
      title: t('moduleToChuc.cotModule'),
      key: 'module',
      render: (_, v) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-content-primary">{v.name}</p>
          <p className="font-mono text-2xs text-content-muted">{v.code}</p>
        </div>
      ),
    },
    {
      title: t('moduleToChuc.cotPhienBan'),
      dataIndex: 'version',
      width: 110,
      render: (v: string) => <span className="font-mono text-xs text-content-primary">{v}</span>,
    },
    {
      title: t('moduleToChuc.cotTrangThai'),
      dataIndex: 'status',
      width: 150,
      render: (_, v) => <BadgeTrangThaiPhienBan trangThai={v.status} />,
    },
    {
      title: t('moduleToChuc.cotNopLuc'),
      dataIndex: 'submitted_at',
      width: 170,
      render: (v: string) => <span className="text-xs text-content-secondary">{ngayGioVN(v, locale)}</span>,
    },
    {
      title: t('moduleToChuc.cotGhiChu'),
      dataIndex: 'review_note',
      responsive: ['lg'],
      render: (v: string | null) => <span className="text-xs text-content-secondary">{v ?? KHONG_CO}</span>,
    },
    {
      key: 'thao-tac',
      width: 56,
      render: (_, v) => (
        <MenuThaoTac
          tenBanGhi={v.name}
          muc={[
            {
              // Xoá cả module (mọi phiên bản), không riêng dòng này: server chỉ cho người nộp mọi bản (ADR-0112)
              khoa: 'xoa',
              nhan: t('moduleToChuc.thaoTac.xoa'),
              icon: <DeleteOutlined />,
              nhom: 'pha-huy',
              onChon: () => xacNhanXoa(v),
            },
          ]}
        />
      ),
    },
  ];

  const cotChoDuyet: TableProps<ModuleVersion>['columns'] = [
    {
      title: t('moduleToChuc.cotModule'),
      key: 'module',
      render: (_, v) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-content-primary">{v.name}</p>
          <p className="font-mono text-2xs text-content-muted">{v.code}</p>
        </div>
      ),
    },
    {
      title: t('moduleToChuc.cotPhienBan'),
      dataIndex: 'version',
      width: 110,
      render: (v: string) => <span className="font-mono text-xs text-content-primary">{v}</span>,
    },
    {
      title: t('moduleToChuc.cotTrangThai'),
      dataIndex: 'status',
      width: 150,
      render: (_, v) => <BadgeTrangThaiPhienBan trangThai={v.status} />,
    },
    {
      title: t('moduleToChuc.cotNopLuc'),
      dataIndex: 'submitted_at',
      width: 170,
      render: (v: string) => <span className="text-xs text-content-secondary">{ngayGioVN(v, locale)}</span>,
    },
    {
      title: t('moduleToChuc.choDuyet.cotDuyetLuc'),
      dataIndex: 'reviewed_at',
      width: 170,
      responsive: ['lg'],
      render: (v: string | null) => (
        <span className="text-xs text-content-secondary">{v ? ngayGioVN(v, locale) : KHONG_CO}</span>
      ),
    },
    {
      title: t('moduleToChuc.cotGhiChu'),
      dataIndex: 'review_note',
      responsive: ['xl'],
      render: (v: string | null) => <span className="text-xs text-content-secondary">{v ?? KHONG_CO}</span>,
    },
    {
      key: 'thao-tac',
      width: 56,
      render: (_, v) => (
        <MenuThaoTac
          tenBanGhi={`${v.name} ${v.version}`}
          muc={[
            {
              khoa: 'duyet',
              nhan: t('moduleToChuc.choDuyet.thaoTacDuyet'),
              icon: <CheckOutlined />,
              hien: v.status === 'IN_REVIEW',
              onChon: () => xacNhanDuyet(v),
            },
            {
              khoa: 'tu-choi',
              nhan: t('moduleToChuc.choDuyet.thaoTacTuChoi'),
              icon: <CloseOutlined />,
              nhom: 'pha-huy',
              hien: v.status === 'IN_REVIEW',
              onChon: () => setTuChoiPhienBan(v),
            },
          ]}
        />
      ),
    },
  ];

  const soChoDuyet = (choDuyet.data ?? []).filter((v) => v.status === 'IN_REVIEW').length;

  const cotBoKhoiTao: TableProps<BoKhoiTao>['columns'] = [
    {
      title: t('moduleToChuc.cotModule'),
      key: 'module',
      render: (_, b) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-content-primary">{b.name}</p>
          <p className="font-mono text-2xs text-content-muted">{b.code}</p>
          {b.description && <p className="mt-0.5 line-clamp-2 text-xs text-content-secondary">{b.description}</p>}
        </div>
      ),
    },
    { title: t('moduleToChuc.cotNhaPhatTrien'), dataIndex: 'vendor', width: 140, responsive: ['md'] },
    {
      title: t('moduleToChuc.cotPhienBan'),
      dataIndex: 'version',
      width: 110,
      render: (v: string) => <span className="font-mono text-xs text-content-primary">{v}</span>,
    },
    {
      title: t('moduleToChuc.boKhoiTao.cotQuyenApi'),
      key: 'quyen-api',
      width: 190,
      responsive: ['lg'],
      render: (_, b) => (
        <span className="text-xs text-content-secondary">
          {t('moduleToChuc.boKhoiTao.soQuyenApi', {
            quyen: soVN(b.permission_count, locale),
            api: soVN(b.route_count, locale),
          })}
        </span>
      ),
    },
    {
      title: t('moduleToChuc.boKhoiTao.cotDungLuong'),
      dataIndex: 'size',
      width: 110,
      responsive: ['lg'],
      render: (v: number) => <span className="text-xs text-content-secondary">{dungLuongVN(v, locale)}</span>,
    },
    {
      key: 'thao-tac',
      width: 56,
      render: (_, b) => (
        <MenuThaoTac
          tenBanGhi={b.name}
          muc={[
            {
              khoa: 'tai',
              nhan: t('moduleToChuc.boKhoiTao.tai'),
              icon: <DownloadOutlined />,
              onChon: () => taiBo(b),
            },
          ]}
        />
      ),
    },
  ];

  const loiBang = (loi: unknown, thuLai: () => void) => (
    <div role="alert" className="flex items-center justify-between gap-3 px-4 py-3">
      <span className="text-sm text-danger-700">{t('moduleToChuc.loiTai', { loi: dichLoi(loi) })}</span>
      <Button variant="outline" size="sm" onClick={thuLai}>
        {t('chung.thuLai')}
      </Button>
    </div>
  );

  const tabs = [
    quyen.xem && {
      key: 'da-duyet',
      label: t('moduleToChuc.tabDaDuyet'),
      children: (
        <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
          {/* Cột trạng thái đọc danh sách đã cài: không đọc được thì báo lỗi, không hiện "Chưa cài" sai. */}
          {daDuyet.error || daCai.error ? (
            loiBang(daDuyet.error ?? daCai.error, () => {
              if (daDuyet.error) void daDuyet.refetch();
              if (daCai.error) void daCai.refetch();
            })
          ) : (
            <DataTable<ModuleDaDuyet>
              caption={t('moduleToChuc.bangDaDuyet')}
              rowKey="code"
              dataSource={daDuyet.data ?? []}
              columns={cotDaDuyet}
              loading={daDuyet.isLoading}
              size="middle"
              pagination={false}
              locale={{
                emptyText: daDuyet.isLoading ? (
                  ' '
                ) : (
                  <Empty
                    icon={Blocks}
                    title={t('moduleToChuc.rongDaDuyet')}
                    description={t('moduleToChuc.rongDaDuyetMo')}
                    action={nutNop}
                  />
                ),
              }}
            />
          )}
        </div>
      ),
    },
    quyen.nop && {
      key: 'goi-cua-toi',
      label: t('moduleToChuc.tabGoiCuaToi'),
      children: (
        <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
          {goiCuaToi.error ? (
            loiBang(goiCuaToi.error, () => void goiCuaToi.refetch())
          ) : (
            <DataTable<ModuleVersion>
              caption={t('moduleToChuc.bangGoiCuaToi')}
              rowKey="version_id"
              dataSource={goiCuaToi.data ?? []}
              columns={cotGoiCuaToi}
              loading={goiCuaToi.isLoading}
              size="middle"
              pagination={false}
              locale={{
                emptyText: goiCuaToi.isLoading ? (
                  ' '
                ) : (
                  <Empty
                    icon={Package}
                    title={t('moduleToChuc.rongGoiCuaToi')}
                    description={t('moduleToChuc.rongGoiCuaToiMo')}
                    action={nutNop}
                  />
                ),
              }}
            />
          )}
        </div>
      ),
    },
    quanTri && {
      key: 'cho-duyet',
      label: soChoDuyet > 0 ? t('moduleToChuc.choDuyet.tabSoLuong', { so: soVN(soChoDuyet, locale) }) : t('moduleToChuc.choDuyet.tab'),
      children: (
        <div className="space-y-3">
          <div className="rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
            <p className="text-sm text-content-secondary">{t('moduleToChuc.choDuyet.moTa')}</p>
          </div>
          <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
            {choDuyet.error ? (
              <div role="alert" className="flex items-center justify-between gap-3 px-4 py-3">
                <span className="text-sm text-danger-700">
                  {t('moduleToChuc.choDuyet.loiTai', { loi: dichLoi(choDuyet.error) })}
                </span>
                <Button variant="outline" size="sm" onClick={() => void choDuyet.refetch()}>
                  {t('chung.thuLai')}
                </Button>
              </div>
            ) : (
              <DataTable<ModuleVersion>
                caption={t('moduleToChuc.choDuyet.bang')}
                rowKey="version_id"
                dataSource={choDuyet.data ?? []}
                columns={cotChoDuyet}
                loading={choDuyet.isLoading}
                size="middle"
                pagination={false}
                locale={{
                  emptyText: choDuyet.isLoading ? (
                    ' '
                  ) : (
                    <Empty
                      icon={ClipboardCheck}
                      title={t('moduleToChuc.choDuyet.rong')}
                      description={t('moduleToChuc.choDuyet.rongMo')}
                    />
                  ),
                }}
              />
            )}
          </div>
        </div>
      ),
    },
    quyen.nop && {
      key: 'bo-phat-trien',
      label: t('moduleToChuc.boKhoiTao.tab'),
      children: (
        <div className="space-y-3">
          <div className="rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
            <p className="text-sm text-content-secondary">{t('moduleToChuc.boKhoiTao.gioiThieu')}</p>
            <p className="mt-1 text-xs text-content-muted">{t('moduleToChuc.boKhoiTao.cacBuoc')}</p>
            {boKhoiTao.data?.sdk_version && (
              <p className="mt-2">
                <Badge variant="neutral">
                  {t('moduleToChuc.boKhoiTao.phienBanSdk', { phienBan: boKhoiTao.data.sdk_version })}
                </Badge>
              </p>
            )}
          </div>
          <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
            {boKhoiTao.error ? (
              <div role="alert" className="flex items-center justify-between gap-3 px-4 py-3">
                <span className="text-sm text-danger-700">
                  {t('moduleToChuc.boKhoiTao.loiTai', { loi: dichLoi(boKhoiTao.error) })}
                </span>
                <Button variant="outline" size="sm" onClick={() => void boKhoiTao.refetch()}>
                  {t('chung.thuLai')}
                </Button>
              </div>
            ) : (
              <DataTable<BoKhoiTao>
                caption={t('moduleToChuc.boKhoiTao.bang')}
                rowKey="code"
                dataSource={boKhoiTao.data?.items ?? []}
                columns={cotBoKhoiTao}
                loading={boKhoiTao.isLoading}
                size="middle"
                pagination={false}
                locale={{
                  emptyText: boKhoiTao.isLoading ? (
                    ' '
                  ) : (
                    <Empty
                      icon={Code2}
                      title={t('moduleToChuc.boKhoiTao.rong')}
                      description={t('moduleToChuc.boKhoiTao.rongMo')}
                    />
                  ),
                }}
              />
            )}
          </div>
        </div>
      ),
    },
  ].filter((tab) => tab !== false);

  const dangTai =
    daDuyet.isFetching || goiCuaToi.isFetching || boKhoiTao.isFetching || daCai.isFetching || choDuyet.isFetching;

  if (!quyen.xem && !quyen.nop) return <ForbiddenView />;

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4 pb-12">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.module') }, { label: t('nav.muc.danhSachModule') }]}
          titleIcon={<Blocks size={18} className="text-primary-600" />}
          title={t('moduleToChuc.tieuDe')}
          subtitle={t('moduleToChuc.moTa')}
          actions={
            <>
              <Button
                variant="outline"
                size="md"
                title={t('chung.lamMoi')}
                aria-label={t('chung.lamMoi')}
                onClick={() => {
                  if (quyen.xem) void daDuyet.refetch();
                  if (quyen.nop) void goiCuaToi.refetch();
                  if (quyen.nop) void boKhoiTao.refetch();
                  if (quanTri) void choDuyet.refetch();
                  void daCai.refetch();
                }}
              >
                <RefreshCw size={15} className={dangTai ? 'animate-spin' : ''} />
              </Button>
              {nutNop}
            </>
          }
        />

        <TabsTruot items={tabs} />
      </div>

      <HopUploadModule open={moUpload} onClose={() => setMoUpload(false)} nop={nopGoi} kenh="tenant" />
      <HopTuChoiModule
        open={tuChoiPhienBan !== null}
        phienBan={tuChoiPhienBan}
        onClose={() => setTuChoiPhienBan(null)}
        tuChoi={tuChoiModule}
      />
    </div>
  );
}
